import { useMemo, useState } from 'react';
import './bottomMenu.css';
import { useDispatch, useSelector } from 'react-redux';
import { resetCurrentCalculationSession } from '../../../redux/Calculations/CalculationsActions';
import FetchData from '../../ServerCall/FetchData';
import { minMax } from '../constants';
import { NotificationDialog } from '../../CommonFunctions/DialogComponents';
import { getFlowTypeFromFunctionName, isFlowUsed, orderByDependency } from '../utils';
import { fontSize, palette } from '../../..';
import deepCopy from 'lodash/cloneDeep';
import { RootState } from '../../../redux';

import FormulaDialog from './FormulaDialog';
import { TAlert } from '../../../types/types';

const TestOrCloseFomula = () => {
  const dispatch = useDispatch();
  const { chartProperties, tabTileProps, calculations } = useSelector((state: RootState) => ({
    chartProperties: state.chartProperties,
    tabTileProps: state.tabTileProps,
    calculations: state.calculations,
  }));
  const [canTestFormula, setCanTestFormula] = useState<boolean>(true);
  const propKey = useMemo(
    () => `${tabTileProps?.selectedTabId}.${tabTileProps?.selectedTileId}`,
    [tabTileProps?.selectedTabId, tabTileProps?.selectedTileId],
  );
  // const flowList = calculations.properties[propKey]?.currentCalculationSession?.calculationInfo.flows;
  const [tableRelationshipStatus, setTableRelationshipStatus] = useState<
    | null
    | {
        table1: string;
        table2: string;
        relationship: string;
        isDirect: boolean;
      }[]
  >(null);

  const [alert, setAlert] = useState<TAlert | null>(null);
  const [lastTestSuccess, setLastTestSuccess] = useState<boolean>(false);
  let calculationInfo = deepCopy(
    calculations.properties[propKey]?.currentCalculationSession?.calculationInfo,
  );
  const currentCalculationSession = deepCopy(
    calculations.properties[propKey]?.currentCalculationSession,
  );
  const [flowRearrangingOrder, setFlowRearrangingOrder] = useState<string[]>([]);
  if (!currentCalculationSession || !calculationInfo) return null;
  const activeFlowId = currentCalculationSession?.activeFlow;

  const selectedDataset = chartProperties.properties[propKey]?.selectedDs;
  const datasetId = selectedDataset?.id;
  const workSpaceId = selectedDataset?.workSpaceId;
  const dbConnectionId = selectedDataset?.connectionId;
  const fields = currentCalculationSession?.calculationInfo?.fields;

  const handleTestCalculation = async () => {
    const flows = currentCalculationSession.calculationInfo?.flows!;

    if (Object.keys(flows).length === 0) {
      if (alert) {
        setAlert(null);
      } else {
        setAlert({
          severity: 'warning',
          message: 'Please create a calculation step before testing.',
        });
      }
      return;
    }

    let flowKeys = Object.keys(flows);
    let counter = 0;

    // Iterate over each flow and check if it's used in other flows or in evaluation of other flows
    let canTest = false;
    for (let i = 0; i < flowKeys.length; i++) {
      const flowId = flowKeys[i];
      const subFlowInfo = currentCalculationSession.calculationInfo.flows[flowId]?.[0];
      if (subFlowInfo && subFlowInfo.overrided) {
        const hasSource = subFlowInfo.source?.[0];
        if (!hasSource) {
          setAlert({
            severity: 'error',
            message: `Flow ${flowId} is not properly configured.`,
          });
          return;
        }
        const overrideInfo = subFlowInfo.flowOverride;
        if (overrideInfo && !overrideInfo.filter.type.includes('All')) {
          const filterGroupSelected = overrideInfo.filter.isFilterGroup;
          if (filterGroupSelected && overrideInfo.filter.filterGroups.length === 0) {
            setAlert({
              severity: 'error',
              message: `No filter group is selected in filter Override`,
            });
            return;
          } else if (!filterGroupSelected && overrideInfo.filter.filters.length === 0) {
            setAlert({
              severity: 'error',
              message: `No filter is selected in filter Override`,
            });
            return;
          }
        }
        if (
          overrideInfo &&
          !overrideInfo.dimension.type.includes('All') &&
          overrideInfo.dimension.dimensions.length === 0
        ) {
          setAlert({
            severity: 'error',
            message: `No dimension is selected in dimension Override`,
          });
          return;
        }
        if (
          overrideInfo &&
          !canTest &&
          (overrideInfo.filter.type !== 'honorAll' || overrideInfo.dimension.type !== 'honorAll')
        ) {
          canTest = false;
          setCanTestFormula(false);
        }
        if (overrideInfo?.windowFunction) {
          canTest = false;
          setCanTestFormula(false);
        }
      }

      const result = isFlowUsed(
        flowKeys,
        flows,
        flowId,
        currentCalculationSession.calculationInfo!,
      );

      if (!result) {
        counter++;
      }
    }

    if (counter > 1) {
      setAlert({
        severity: 'error',
        message: 'There are unused flows, so cannot proceed with testing the formula.',
      });
      return;
    }

    const firstFlowId = Object.keys(currentCalculationSession?.calculationInfo?.flows)[0];

    const activeFlowName =
      currentCalculationSession?.calculationInfo?.flows[
        activeFlowId ? activeFlowId : firstFlowId
      ][0]?.flow;
    const activeFlowType = currentCalculationSession?.activeFlowType
      ? currentCalculationSession?.activeFlowType
      : getFlowTypeFromFunctionName(activeFlowName);

    const sourceTypes =
      currentCalculationSession?.calculationInfo?.flows[
        activeFlowId ? activeFlowId : firstFlowId
      ][0]?.sourceType;

    if (
      !sourceTypes.includes('field') &&
      !sourceTypes.includes('flow') &&
      activeFlowType !== 'Condition' &&
      !['currentDate', 'currentTimestamp'].includes(activeFlowName)
    ) {
      if (alert) {
        setAlert(null);
      } else {
        setAlert({
          severity: 'warning',
          message:
            activeFlowName === 'stringToDate'
              ? 'Something is not right, please check if the format you provided is correct'
              : 'Please add at least one column from the table to proceed.',
        });
      }
      return;
    }

    const tableList = Object.keys(fields).reduce((acc: any, index: string) => {
      if (!acc.includes(fields[index].tableId)) {
        acc.push(fields[index].tableId);
      }
      return acc;
    }, []);

    if (tableList.length > 1) {
      const tableRelationResponse = await FetchData({
        url: `table-relationship?workspaceId=${workSpaceId}&dbconnectionid=${dbConnectionId}&datasetId=${datasetId}`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        requestType: 'withData',
        data: {
          tableIds: tableList,
        },
      });

      if (tableRelationResponse.status === true) {
        // TODO: the below check needs further improvement. This implementation is in the initial stage of the feature

        const tableRelation = tableRelationResponse.data;

        setTableRelationshipStatus(tableRelation);

        const relationship = tableRelation[0].relationship;
        const isDirect = tableRelation[0].isDirect;

        if (
          (relationship !== 'one to many' &&
            relationship !== 'many to one' &&
            relationship !== 'one to one') ||
          !isDirect
        ) {
          setAlert({
            severity: 'warning',
            message: 'The relationship between chosen tables must be one to many and direct.',
          });
          return;
        }
      } else {
        setAlert({
          severity: 'error',
          message: 'Something went wrong.',
        });
      }
    }

    const res = orderByDependency(calculationInfo);
    if (res.error) {
      setAlert({ severity: 'error', message: res.message });
      return;
    }
    const sortedDependencyStack = res.orderedFlows;
    const lastFlowId = sortedDependencyStack[sortedDependencyStack.length - 1];
    const lastFlow = calculationInfo.flows[lastFlowId]?.[0];

    if (!lastFlow) {
      setAlert({
        severity: 'error',
        message: `No flow data found .`,
      });
      return;
    }
    const currentDataType =
      lastFlow.flow === 'IfElse'
        ? calculationInfo.selectedResultType[lastFlowId]
        : lastFlow.resultantDatatype;

    if (!currentDataType) {
      setAlert({
        severity: 'error',
        message: `Datatype not found for the last flow ${lastFlow.flow ?? ''}. Please check the calculation.`,
      });
      return;
    }
    const currentAgg = lastFlow.isAggregation;

    setFlowRearrangingOrder(sortedDependencyStack);

    if (currentCalculationSession?.uuid) {
      const matchedCalculation = calculations.savedCalculations.find((savedCalc) => {
        return savedCalc.uuid === currentCalculationSession.uuid;
      });

      if (!matchedCalculation) {
        setAlert({
          severity: 'warning',
          message: 'No saved calculation matches the current session.',
        });
        return;
      }

      if (currentDataType !== matchedCalculation.datatype) {
        setAlert({
          severity: 'error',
          message: `Datatype mismatch. Expected ${matchedCalculation.datatype} but got ${currentDataType}.`,
        });
        return;
      } else if (currentAgg !== matchedCalculation.isAggregated) {
        setAlert({
          severity: 'error',
          message: `Aggregation mismatch. Expected ${matchedCalculation.isAggregated ? 'Visual aggregation' : 'Row level calculation '} but got ${currentAgg ? 'Visual aggregation' : 'Row level calculation '}.`,
        });
        return;
      }
    }

    if (activeFlowType === 'Number') {
      if (Object.keys(currentCalculationSession?.calculationInfo?.flows).length === 0) {
        if (alert) {
          setAlert(null);
        } else {
          setAlert({
            severity: 'warning',
            message: 'Please create a flow before testing.',
          });
        }
        return;
      }

      if (
        [
          'absolute',
          'addition',
          'ceiling',
          'floor',
          'division',
          'min',
          'max',
          'multiplication',
          'subtraction',
        ].includes(activeFlowName)
      ) {
        // check if minimum sources requirement is met for the current calculation if not show warning dialog
        if (
          !(
            minMax[`${activeFlowType}`][`${activeFlowName}`]?.min <=
              currentCalculationSession.calculationInfo.flows[
                activeFlowId ? activeFlowId : firstFlowId
              ][0].sourceType.filter(
                (src: any) =>
                  src === 'field' || src === 'decimal' || src === 'flow' || src === 'integer',
              ).length &&
            minMax[`${activeFlowType}`][`${activeFlowName}`]?.max >=
              currentCalculationSession.calculationInfo.flows[
                activeFlowId ? activeFlowId : firstFlowId
              ][0].sourceType.filter(
                (src: any) => src === 'field' || src === 'decimal' || src === 'integer',
              ).length
          )
        ) {
          if (alert) {
            setAlert(null);
          } else {
            setAlert({
              severity: 'warning',
              message: 'Please add more sources before testing',
            });
          }
          return;
        }

        setLastTestSuccess(true);
      } else if (['log'].includes(activeFlowName)) {
        const sourceTypes =
          currentCalculationSession?.calculationInfo?.flows[
            activeFlowId ? activeFlowId : firstFlowId
          ][0]?.sourceType;

        // if (sourceTypes.length !== 2 || (sourceTypes[0] !== 'field' && sourceTypes[0] !== 'flow')) {
        //   if (alert) {
        //     setAlert(null)
        //   }
        //   setAlert({ severity: 'warning', message: 'Please add more sources before testing.' })
        //   return
        // } else {
        //   setLastTestSuccess(true);
        // }

        if (sourceTypes.length < 2) {
          setAlert({
            severity: 'warning',
            message: 'Please add more sources before testing',
          });
          return;
        } else if (
          sourceTypes.length === 2 &&
          sourceTypes.includes('field') &&
          sourceTypes[0] !== 'field'
        ) {
          setAlert({ severity: 'warning', message: 'Something went wrong' });
          return;
        } else if (
          sourceTypes.length === 2 &&
          sourceTypes.includes('flow') &&
          sourceTypes[0] !== 'flow'
        ) {
          setAlert({ severity: 'warning', message: 'Something went wrong' });
          return;
        } else {
          setLastTestSuccess(true);
        }
      } else if (activeFlowName === 'power') {
        const src =
          currentCalculationSession.calculationInfo.flows[
            activeFlowId ? activeFlowId : firstFlowId
          ][0].source;
          const isValidPower=src[1]
          if (isValidPower === '' || isNaN(isValidPower) || Number(isValidPower) < 0|| Number(isValidPower) > 10) {
            if (alert) {
              setAlert(null);
            }
            setAlert({
              severity: 'warning',
              message: 'Please provide a valid non-negative number as power value between 0 to 10',
            });
            return;
          }
        if (src.length === 2) {
          setLastTestSuccess(true);
          return;
        }
      }
    } else if (activeFlowType === 'Condition') {
      if (activeFlowName === 'IfElse') {
        // const sourceList = currentCalculationSession?.calculationInfo?.flows[activeFlowId ? activeFlowId : firstFlowId][0]?.source
        const flowArray =
          currentCalculationSession?.calculationInfo?.flows[
            activeFlowId ? activeFlowId : firstFlowId
          ];
        for (let i = 0; i < flowArray.length; i++) {
          const condition = flowArray[i];

          /**
           * for if else source array has to be  of length 1
           */
          if (condition.source.length !== 1) {
            if (alert) {
              setAlert(null);
            }
            setAlert({
              severity: 'warning',
              message: 'One or more fields are not filled properly',
            });
            return;
          }
          if (['if', 'elseIf'].includes(condition.condition ?? '')) {
            /**
             * as of now in evaluation only columns can be dropped
             *
             */
            const evaluationId = condition.filter;
            if (
              !evaluationId ||
              !currentCalculationSession?.calculationInfo?.conditionFilters?.[evaluationId]
            ) {
              if (alert) {
                setAlert(null);
              }
              setAlert({
                severity: 'warning',
                message: 'One or more fields are not filled properly',
              });
              return;
            }
            const evaluations =
              currentCalculationSession?.calculationInfo?.conditionFilters?.[evaluationId][0]
                .conditions;
            if (evaluations.length > 0) {
              setLastTestSuccess(true);
              return;
            }
          } else if (condition.condition === 'else') {
            setLastTestSuccess(true);
            return;
          }
          if (alert) {
            setAlert(null);
          }
          setAlert({
            severity: 'warning',
            message: 'One or more fields are not filled properly',
          });
          return;
        }
      }
    } else if (activeFlowType === 'Date') {
      // TODO: do this after discussing with Anirudha the great samurai
      if (['datePartName', 'datePartNumber', 'truncateDate'].includes(activeFlowName)) {
        const sources =
          currentCalculationSession?.calculationInfo?.flows[
            activeFlowId ? activeFlowId : firstFlowId
          ][0]?.source;
        if (sources.length === 2) {
          // no need to show warning dialog
          setLastTestSuccess(true);
        } else {
          if (alert) {
            setAlert(null);
            setAlert({
              severity: 'warning',
              message: 'Please add two sources before testing.',
            });
          } else {
            setAlert({
              severity: 'warning',
              message: 'Please add two sources before testing.',
            });
          }
          return;
        }
      } else if (['currentDate', 'currentTimestamp'].includes(activeFlowName)) {
        const sources =
          currentCalculationSession?.calculationInfo?.flows[
            activeFlowId ? activeFlowId : firstFlowId
          ][0]?.source;

        if (sources.length === 0) {
          setLastTestSuccess(true);
        } else {
          if (alert) {
            setAlert(null);
            setAlert({
              severity: 'warning',
              message: 'Please add one source before testing.',
            });
            return;
          } else {
            setAlert({
              severity: 'warning',
              message: 'Please add one source before testing.',
            });
            return;
          }
        }
      } else if (['minDate', 'maxDate'].includes(activeFlowName)) {
        const sources =
          currentCalculationSession?.calculationInfo?.flows[
            activeFlowId ? activeFlowId : firstFlowId
          ][0]?.source;
        const sourceTypes =
          currentCalculationSession?.calculationInfo?.flows[
            activeFlowId ? activeFlowId : firstFlowId
          ][0]?.sourceType;

        if (sources.length === 1 && (sourceTypes[0] === 'field' || sourceTypes[0] === 'flow')) {
          setLastTestSuccess(true);
        } else {
          if (alert) {
            setAlert(null);
            setAlert({
              severity: 'warning',
              message: 'Please add one source before testing.',
            });
            return;
          } else {
            setAlert({
              severity: 'warning',
              message: 'Please add one source before testing.',
            });
            return;
          }
        }
      } else if (activeFlowName === 'stringToDate') {
        const sources =
          currentCalculationSession?.calculationInfo?.flows[
            activeFlowId ? activeFlowId : firstFlowId
          ][0]?.source;

        if (sources.length >= 4) {
          // good to go no need to alert
          setLastTestSuccess(true);
        } else {
          if (alert) {
            setAlert(null);
            setAlert({
              severity: 'warning',
              message: 'Please add required sources before testing.',
            });
            return;
          } else {
            setAlert({
              severity: 'warning',
              message: 'Please add required sources before testing.',
            });
            return;
          }
        }
      } else if (activeFlowName === 'dateInterval') {
        const sources =
          currentCalculationSession?.calculationInfo?.flows[
            activeFlowId ? activeFlowId : firstFlowId
          ][0]?.source;
        if (sources.length === 3 && !sources.includes(null) && !sources.includes(undefined)) {
          setLastTestSuccess(true);
        } else {
          if (alert) {
            setAlert(null);
            setAlert({
              severity: 'warning',
              message: 'Please add required sources before testing.',
            });
            return;
          } else {
            setAlert({
              severity: 'warning',
              message: 'Please add required sources before testing.',
            });
            return;
          }
        }
      } else if (activeFlowName === 'addDateInterval') {
        const sources =
          currentCalculationSession?.calculationInfo?.flows[
            activeFlowId ? activeFlowId : firstFlowId
          ][0]?.source;

        if (sources.length === 3 && sources[1] !== '') {
          setLastTestSuccess(true);
        } else {
          if (alert) {
            setAlert(null);
            setAlert({
              severity: 'warning',
              message: 'Please add required sources before testing.',
            });
            return;
          } else {
            setAlert({
              severity: 'warning',
              message: 'Please add required sources before testing.',
            });
            return;
          }
        }
      }
    } else if (activeFlowType === 'String') {
      if (
        ['lowercase', 'uppercase', 'propercase', 'trim', 'length', 'ltrim', 'rtrim'].includes(
          activeFlowName,
        )
      ) {
        const sourceTypes = currentCalculationSession?.calculationInfo?.flows[
          activeFlowId ? activeFlowId : firstFlowId
        ][0]?.sourceType.filter((type: string) => {
          return type === 'field' || type === 'flow';
        });
        if (sourceTypes.length === 1) {
          // no need to show warning dialog
          setLastTestSuccess(true);
        } else {
          if (alert) {
            setAlert(null);
            setAlert({
              severity: 'warning',
              message: 'Please add required sources before testing.',
            });
          } else {
            setAlert({
              severity: 'warning',
              message: 'Please add required sources before testing.',
            });
          }
          return;
        }
      } else if (['substringleft', 'substringright'].includes(activeFlowName)) {
        const sourceTypes =
          currentCalculationSession?.calculationInfo?.flows[
            activeFlowId ? activeFlowId : firstFlowId
          ][0]?.sourceType;

        if (
          sourceTypes.length === 3 &&
          (sourceTypes[0] === 'field' || sourceTypes[0] === 'flow') &&
          sourceTypes[1] === 'decimal' &&
          sourceTypes[2] === 'text'
        ) {
          setLastTestSuccess(true);
        } else {
          if (alert) {
            setAlert(null);
            setAlert({
              severity: 'warning',
              message: 'Please add required sources before testing.',
            });
          } else {
            setAlert({
              severity: 'warning',
              message: 'Please add required sources before testing.',
            });
          }
          return;
        }
      } else if (activeFlowName === 'split') {
        const sourceTypes =
          currentCalculationSession?.calculationInfo?.flows[
            activeFlowId ? activeFlowId : firstFlowId
          ][0]?.sourceType;
        const sources =
          currentCalculationSession?.calculationInfo?.flows[
            activeFlowId ? activeFlowId : firstFlowId
          ][0]?.source;

        if (
          sourceTypes.length === 4 &&
          (sourceTypes[0] === 'field' || sourceTypes[0] === 'flow') &&
          sourceTypes[1] === 'text' &&
          sources[1].length > 0 &&
          sourceTypes[2] === 'decimal' &&
          sourceTypes[3] === 'text'
        ) {
          setLastTestSuccess(true);
        } else {
          if (alert) {
            setAlert(null);
            setAlert({
              severity: 'warning',
              message: 'Please add required sources before testing.',
            });
          } else {
            setAlert({
              severity: 'warning',
              message: 'Please add required sources before testing.',
            });
          }
          return;
        }
      } else if (activeFlowName === 'replace') {
        const sourceTypes =
          currentCalculationSession?.calculationInfo?.flows[
            activeFlowId ? activeFlowId : firstFlowId
          ][0]?.sourceType;
        const sources =
          currentCalculationSession?.calculationInfo?.flows[
            activeFlowId ? activeFlowId : firstFlowId
          ][0]?.source;

        if (
          sourceTypes.length === 3 &&
          (sourceTypes[0] === 'field' || sourceTypes[0] === 'flow') &&
          sourceTypes[1] === 'text' &&
          sources[1].length > 0 &&
          sourceTypes[2] === 'text' &&
          sources[2].length > 0
        ) {
          setLastTestSuccess(true);
        } else {
          if (alert) {
            setAlert(null);
            setAlert({
              severity: 'warning',
              message: 'Please add required sources before testing.',
            });
          } else {
            setAlert({
              severity: 'warning',
              message: 'Please add required sources before testing.',
            });
          }
          return;
        }
      } else if (activeFlowName === 'concat') {
        const sourceTypes =
          currentCalculationSession?.calculationInfo?.flows[
            activeFlowId ? activeFlowId : firstFlowId
          ][0]?.sourceType;
        const sources =
          currentCalculationSession?.calculationInfo?.flows[
            activeFlowId ? activeFlowId : firstFlowId
          ][0]?.source;

        if (
          sourceTypes.length >= 2 &&
          sourceTypes.length <= 6 &&
          sourceTypes.every((type) => type === 'field' || type === 'flow' || type === 'text') &&
          sources.every((source) => source.length > 0)
        ) {
          setLastTestSuccess(true);
        } else {
          if (alert) {
            setAlert(null);
            setAlert({
              severity: 'warning',
              message: 'Please add required sources before testing.',
            });
          } else {
            setAlert({
              severity: 'warning',
              message: 'Please add required sources before testing.',
            });
          }
          return;
        }
      }
    } else if (activeFlowType === 'Aggregation') {
      setLastTestSuccess(true);
    } else if (activeFlowType === 'Datatype Conversion') {
      setLastTestSuccess(true);
      return;
    }
  };

  return (
    <div className="bottom-menu-container">
      {alert && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={!!alert}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}

      <div
        style={{
          marginLeft: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          width: '228px',
          justifyContent: 'space-between',
          marginBottom: '0.5rem',
        }}
      >
        <button
          onClick={() => {
            dispatch(resetCurrentCalculationSession(propKey));
          }}
          style={{
            color: palette.primary.contrastText,
            background: 'white',
            padding: '3px 5px',
            height: '2rem',
            flex: 1,
            border: '1px solid rgb(211, 211, 211)',
            borderRadius: '3px',
            fontSize: fontSize.medium,
          }}
        >
          Close
        </button>
        {/* {
          Object.keys(currentCalculationSession?.calculationInfo?.flows).length > 0 && 
        } */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleTestCalculation();
          }}
          style={{
            background: palette.primary.main,
            color: 'white',
            border: 'none',
            height: '2rem',
            padding: '3px 5px',
            flex: 1,
            borderRadius: '3px',
            fontSize: fontSize.medium,
          }}
        >
          Test formula
        </button>
      </div>
      {lastTestSuccess && (
        <FormulaDialog
          propKey={propKey}
          showPopup={lastTestSuccess}
          orderedListOfFlows={flowRearrangingOrder}
          workspaceId={workSpaceId}
          datasetId={datasetId}
          dbConnectionId={dbConnectionId}
          onClose={() => {
            setLastTestSuccess(false);
            setCanTestFormula(true);
          }}
          tableRelationships={tableRelationshipStatus}
          canTestFormula={canTestFormula}
          extractedDs={selectedDataset.isExtract}
        />
      )}
    </div>
  );
};
export default TestOrCloseFomula;
